import { useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveTemplate } from '@vibesites/runtime';
import {
  useContentToken,
  useProperty,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';

interface HeroSolidBGProps {
  tokenPrefix?: string;
  title?: string;
  blurbField?: string;
  defaultText?: string;
  /**
   * Optional large headline. Retained in the schema so per-page pages.json
   * data keeps working, but the Aion inner-page hero does not render it
   * (title + optional blurb sit on the solid navy panel instead).
   */
  headline?: string;
  /** Show the pill CTA button. Retained in schema; not rendered by this override. */
  showCta?: boolean;
  ctaLabel?: string;
  ctaUrl?: string;
  ctaUrlField?: string;
  ctaTarget?: string;
  /** Optional default heading rendered under the title inside the navy panel. */
  blurbHeading?: string;
}

function stripHtml(html: string): string {
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
  return html.replace(/<[^>]*>/g, '').trim();
}

function hasBlurbContent(value: string | null | undefined): boolean {
  return Boolean(stripHtml(value || '').trim());
}

// Schema-only slot groups — kept so per-page pages.json props (bg_image,
// cta_link) still round-trip through the editor even though the Aion
// inner-page hero does not render them. The corresponding wrappers below
// are rendered hidden to satisfy the schema linter.
const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Page hero image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Hero CTA Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: '' },
    url: { label: 'Button URL', type: 'url', defaultValue: '#' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function HeroSolidBG({
  tokenPrefix,
  title = 'Page',
  blurbField,
  defaultText,
  blurbHeading,
}: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  const titlePath = `${prefix}.title`;
  const headlinePath = `${prefix}.headline`;
  const blurbPath = `${prefix}.blurb`;
  const blurbHeadingPath = `${prefix}.blurb_heading`;
  const bgPath = `${prefix}.bg_image`;
  const ctaPath = `${prefix}.cta_link`;

  const blurbContent = useContentToken(blurbPath, {
    defaultField: blurbField,
    defaultValue: defaultText || '',
  });
  const showBlurb = hasBlurbContent(blurbContent);

  const property = useProperty() as Record<string, any>;
  const resolvedBlurbHeading = blurbHeading
    ? resolveTemplate(blurbHeading, property)
    : '';
  const blurbHeadingDefault = resolvedBlurbHeading ? `<p>${resolvedBlurbHeading}</p>` : '';
  const blurbHeadingContent = useContentToken(blurbHeadingPath, {
    defaultValue: blurbHeadingDefault,
  });
  const hasBlurbHeading = hasBlurbContent(blurbHeadingContent);

  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="montserrat"]')) return;
    if (!document.querySelector('link[data-vibe-font-preconnect="google"]')) {
      const preconnect1 = document.createElement('link');
      preconnect1.rel = 'preconnect';
      preconnect1.href = 'https://fonts.googleapis.com';
      preconnect1.setAttribute('data-vibe-font-preconnect', 'google');
      document.head.appendChild(preconnect1);
      const preconnect2 = document.createElement('link');
      preconnect2.rel = 'preconnect';
      preconnect2.href = 'https://fonts.gstatic.com';
      preconnect2.crossOrigin = 'anonymous';
      preconnect2.setAttribute('data-vibe-font-preconnect', 'gstatic');
      document.head.appendChild(preconnect2);
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,700&display=swap';
    link.setAttribute('data-vibe-font', 'montserrat');
    document.head.appendChild(link);
  }, []);

  return (
    <section className="hero-solid-bg">
      {/* Aion inner-page hero — solid navy panel with centered title and
         optional blurb heading + blurb stacked underneath. No photo,
         no eyebrow/headline, no CTA button. */}
      <div className="hero-solid-hero relative w-full overflow-hidden">
        <div className="hero-solid-title-wrap relative z-10 flex h-full flex-col items-center justify-center px-6 sm:px-8 text-center text-white">
          <RichTextContent
            path={titlePath}
            defaultValue={`<p>${title}</p>`}
            as="h1"
            className="hero-solid-title"
          />
          {showBlurb || hasBlurbHeading ? (
            <div className="hero-solid-blurb">
              <RichTextContent
                path={blurbHeadingPath}
                defaultValue={blurbHeadingDefault}
                as="h2"
                className="hero-solid-blurb-heading"
              />
              <RichTextContent
                path={blurbPath}
                defaultField={blurbField}
                defaultValue={defaultText || ''}
                as="div"
                className="hero-solid-blurb-text font-body text-base md:text-lg leading-relaxed [&_a]:underline"
              />
            </div>
          ) : null}
        </div>
      </div>

      {/* Dynamic-path hidden wrappers for schema keys the visible tree
         doesn't render (headline, cta_link, bg_image, and blurb fields
         when empty). These use the tokenPrefix-derived paths so per-page
         overrides save to the right DB row (schema-linter rule
         shared-component-missing-dynamic-path). */}
      {!(showBlurb || hasBlurbHeading) ? (
        <>
          <RichTextContent
            path={blurbHeadingPath}
            defaultValue={blurbHeadingDefault}
            as="span"
            className="hidden"
            style={{ display: 'none' }}
          />
          <RichTextContent
            path={blurbPath}
            defaultField={blurbField}
            defaultValue={defaultText || ''}
            as="span"
            className="hidden"
            style={{ display: 'none' }}
          />
        </>
      ) : null}
      <RichTextContent
        path={headlinePath}
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath={bgPath}
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath={ctaPath}
        definition={ctaLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      {/* Literal-path schema-lint anchors — hidden, never interactive.
         Required by the schema linter to match the contentSchema's keys
         exactly one-to-one. */}
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.headline"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.blurb_heading"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosolidbg.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="herosolidbg.cta_link"
        definition={ctaLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        .hero-solid-hero {
          /* Solid panel behind the title. Uses the theme's primary color so
             inner-page heros carry the brand accent; falls back to a dark
             navy that matches the reference screenshot. */
          background: var(--vibe-color-hero-solid-bg, var(--vibe-color-primary, #0B1A3A));
          /* Shorter than the photographic base treatment — the panel is
             intended as a compact page header, not a landing image. */
          min-height: 44vh;
          min-height: 44svh;
          min-height: 44dvh;
        }
        /* The site header floats over this section, so pad the vertically-
           centered title below the nav (plus any offer banner). Padding
           scales with breakpoint so the h1 always sits on the panel's
           visual center below the nav. */
        .hero-solid-title-wrap {
          /* Extra top pad on mobile so the title sits lower under the
             floating nav, rather than hugging the header. */
          padding-top: calc(var(--offer-banner-height, 0px) + var(--top-bar-height, 0px) + 10.5rem);
          padding-bottom: 3rem;
        }
        @media (min-width: 768px) {
          .hero-solid-title-wrap {
            padding-top: calc(var(--offer-banner-height, 0px) + var(--top-bar-height, 0px) + 10rem);
            padding-bottom: 5rem;
          }
        }
        @media (min-width: 1024px) {
          .hero-solid-title-wrap {
            padding-top: calc(var(--offer-banner-height, 0px) + var(--top-bar-height, 0px) + 11rem);
            padding-bottom: 5.5rem;
          }
        }
        .hero-solid-title {
          font-family: var(--vibe-font-heading), sans-serif;
          font-size: 32px;
          font-weight: 400;
          line-height: 1.1;
          letter-spacing: 0;
          color: #fff;
          margin: 0;
          max-width: 24ch;
        }
        .hero-solid-title p,
        .hero-solid-title span {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          margin: 0;
        }
        @media (min-width: 768px) {
          .hero-solid-title { font-size: 36px; }
        }
        @media (min-width: 1024px) {
          .hero-solid-title { font-size: 44px; }
        }

        .hero-solid-blurb {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          margin-top: 1.5rem;
          max-width: 60rem;
          width: 100%;
        }
        @media (min-width: 768px) {
          .hero-solid-blurb {
            gap: 1.25rem;
            margin-top: 1.75rem;
          }
        }
        .hero-solid-blurb-heading {
          font-family: var(--vibe-font-heading), sans-serif;
          font-size: 0.875rem;
          font-weight: 400;
          line-height: 1.2;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.85);
          margin: 0;
          max-width: 40ch;
        }
        .hero-solid-blurb-heading p,
        .hero-solid-blurb-heading span {
          font: inherit;
          color: inherit;
          letter-spacing: inherit;
          text-transform: inherit;
          margin: 0;
        }
        .hero-solid-blurb-heading:empty {
          display: none;
        }
        .hero-solid-blurb-text {
          color: rgba(255, 255, 255, 0.9);
          margin: 0;
          max-width: 80ch;
        }
        .hero-solid-blurb-text:empty {
          display: none;
        }
        .hero-solid-blurb-text a {
          color: inherit;
        }
        .hero-solid-blurb-text p {
          margin: 0;
        }
        .hero-solid-blurb-text p + p {
          margin-top: 1rem;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Hero – Solid Background',
  tokens: {
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosolidbg.headline': {
      label: 'Headline',
      type: 'richtext',
      defaultValue: '',
    },
    'herosolidbg.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
    'herosolidbg.blurb_heading': {
      label: 'Blurb Section Heading',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'herosolidbg.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Page hero image',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
    'herosolidbg.cta_link': {
      label: 'Hero CTA Button',
      fields: {
        label: { label: 'Button Text', type: 'text', defaultValue: '' },
        url: { label: 'Button URL', type: 'url', defaultValue: '#' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
